import React from "react";
import { motion } from "framer-motion";
import { Flag, Landmark, Target, Users } from "lucide-react";
import {
  aboutCooperationPoints,
  aboutHistoryHighlights,
  aboutIdentityCards,
  aboutMission,
  aboutMotto,
} from "../../constants/siteContent";
import { fadeInUp, staggerContainer } from "../../lib/animations";

const aboutFeatureCards = [
  { title: "Mission", body: aboutMission, icon: Target },
  { title: "Motto", body: aboutMotto, icon: Flag },
];

export const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-28 bg-[#F8F6F1]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16 md:mb-[4.5rem]">
          <motion.p variants={fadeInUp} className="text-[#D1111A] font-semibold tracking-[0.2em] uppercase mb-3">About KPL</motion.p>
          <motion.h2 variants={fadeInUp} className="font-editorial text-4xl md:text-6xl text-[#0A0A0A] italic mb-5">Mission, History and Identity</motion.h2>
          <motion.p variants={fadeInUp} className="mx-auto max-w-4xl text-base md:text-lg leading-relaxed text-[#0A0A0A]/72">
            Kenya Premier League Ltd carries a professional football story shaped by club ownership, national purpose, and a long-standing drive to raise standards on and off the field.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid gap-6 md:gap-7 md:grid-cols-3">
          {aboutIdentityCards.map((card, index) => (
            <motion.div key={card.title} variants={fadeInUp} className="border border-[#E4E4E7] bg-white p-6 md:p-7 shadow-sm" data-testid={`about-identity-card-${index}`}>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#D1111A]">{card.title}</p>
              <p className="text-sm leading-relaxed text-[#0A0A0A]/76">{card.body}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="mt-14 grid gap-6 md:gap-7 md:grid-cols-2">
          {aboutFeatureCards.map((card, index) => (
            <motion.div key={card.title} variants={fadeInUp} className="bg-[#0A0A0A] text-white p-7 md:p-8" data-testid={`about-feature-card-${index}`}>
              <card.icon className="w-9 h-9 text-[#D1111A] mb-5" />
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">{card.title}</p>
              <p className="text-base md:text-lg leading-relaxed text-white/88">{card.body}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="mt-16">
          <motion.div variants={fadeInUp} data-testid="about-cooperation-block">
            <div className="mb-6 flex items-center gap-3">
              <Users className="w-6 h-6 text-[#D1111A]" />
              <h3 className="font-editorial text-3xl text-[#0A0A0A] italic">Cooperation with FKF</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {aboutCooperationPoints.map((point, index) => (
                <div key={index} className="bg-white border border-[#E4E4E7] px-5 py-5 shadow-sm" data-testid={`about-cooperation-point-${index}`}>
                  <p className="text-sm leading-relaxed text-[#0A0A0A]/76">{point}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="mt-16">
          <motion.div variants={fadeInUp} className="mb-6 flex items-center gap-3" data-testid="about-history-heading-block">
            <Landmark className="w-6 h-6 text-[#D1111A]" />
            <h3 className="font-editorial text-3xl text-[#0A0A0A] italic">History Highlights</h3>
          </motion.div>

          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#E4E4E7] md:hidden" />
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#E4E4E7] -translate-x-1/2" />

            <div className="space-y-10 md:space-y-12">
              {aboutHistoryHighlights.map((item, index) => (
                <motion.div
                  key={`${item.year}-${item.title}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className={`relative flex w-full flex-col items-start gap-8 pl-12 md:pl-0 md:items-center ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                  data-testid={`about-history-card-${index}`}
                >
                  <div className="absolute left-4 top-6 flex -translate-x-1/2 items-center justify-center md:hidden" data-testid={`about-history-mobile-dot-${index}`}>
                    <span className="h-4 w-4 rounded-full border-4 border-[#F8F6F1] bg-[#D1111A] shadow-[0_0_0_2px_rgba(209,17,26,0.18)]" />
                  </div>

                  <div className={`w-full flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                    <div className={`inline-block w-full max-w-xl bg-white px-6 py-6 md:px-7 md:py-7 shadow-sm border border-[#E4E4E7] hover:bg-[#D1111A] hover:text-white transition-colors group ${index % 2 === 0 ? "md:ml-auto" : ""}`}>
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#D1111A] group-hover:text-white/80">{item.year}</p>
                      <h4 className="mb-3 font-editorial text-2xl leading-tight text-[#0A0A0A] italic group-hover:text-white">{item.title}</h4>
                      <p className="text-sm leading-relaxed text-[#0A0A0A]/76 group-hover:text-white/84">{item.body}</p>
                    </div>
                  </div>

                  <div className="hidden md:flex items-center justify-center w-8">
                    <div className="w-4 h-4 rounded-full bg-[#D1111A] border-4 border-[#F8F6F1] shadow-lg" />
                  </div>

                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};